import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/outline';
import { Text } from 'components/Text';
import { NavItem } from 'types/sidebar';

type SidebarNavChildrenProps = {
  item: NavItem;
  showChildren: boolean;
  level?: number;
  setSidebarOpen?: (value: boolean) => void;
};

export const SidebarNavChildren = ({
  item,
  showChildren,
  level = 1,
  setSidebarOpen,
}: SidebarNavChildrenProps) => {
  const [showSubChildren, setShowSubChildren] = useState(false);
  const location = useLocation();

  if (!showChildren) return null;

  const paddingLeft = level === 1 ? 'pl-5' : level === 2 ? 'pl-10' : 'pl-14';
  const iconSize = level === 1 ? 'h-6 w-6' : 'h-5 w-5';

  // If item has children (3rd level), render as expandable
  if (item.children) {
    return (
      <div className={paddingLeft}>
        <div
          className={classNames(
            location.pathname.split('/').includes(item.key)
              ? 'bg-slate-700/50 text-slate-50'
              : 'text-slate-50 hover:bg-slate-700/50',
            'mx-2 rounded-md group flex items-center px-3 py-3 text-sm font-medium cursor-pointer justify-between',
          )}
          onClick={() => setShowSubChildren(!showSubChildren)}
        >
          <div className="group flex items-center">
            {item.icon && (
              <item.icon
                className={classNames('mr-3 flex-shrink-0', iconSize)}
                aria-hidden="true"
              />
            )}
            {item.name}
          </div>
          {!showSubChildren ? (
            <ChevronRightIcon className="text-slate-400 group-hover:text-slate-200 flex-shrink-0 h-5 w-5" />
          ) : (
            <ChevronDownIcon className="text-slate-400 group-hover:text-slate-200 flex-shrink-0 h-5 w-5" />
          )}
        </div>

        {/* Render 3rd level children */}
        {showSubChildren &&
          item.children.map((subItem) => (
            <SidebarNavChildren
              key={subItem.key}
              item={subItem}
              showChildren={true}
              level={level + 1}
              setSidebarOpen={setSidebarOpen}
            />
          ))}
      </div>
    );
  }

  // Regular nav link (no children)
  return (
    <nav className={paddingLeft + ' flex-1 space-y-1'} key={item.name}>
      <NavLink
        to={item.to || ''}
        target={item.target || undefined}
        className={({ isActive }) =>
          classNames(
            isActive
              ? 'bg-slate-700/50 text-slate-50'
              : 'text-slate-50 hover:bg-slate-700/50',
            'mx-2 rounded-md group flex items-center px-3 py-3 text-sm font-medium justify-between',
          )
        }
        onClick={() => {
          item.onClick?.();
          setSidebarOpen?.(false);
        }}
      >
        <div className="group flex items-center">
          {item.icon && (
            <item.icon
              className={classNames(
                location.pathname.split('/').includes(item.key)
                  ? 'text-slate-50'
                  : 'text-slate-400 group-hover:text-slate-200',
                'mr-3 flex-shrink-0',
                iconSize,
              )}
              aria-hidden="true"
            />
          )}
          {item.name}
        </div>
        {item.count && item.count > 0 ? (
          <div className="bg-slate-500 text-center rounded-full h-5 w-7">
            <Text
              value={`${item.count}`}
              className="leading-loose text-white text-[10px]"
            />
          </div>
        ) : null}
      </NavLink>
    </nav>
  );
};
